import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import React from 'react';

import { useTranslation } from 'react-i18next';
import { MdMarkEmailRead } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import apple_store_icon from '../assets/apple_store.svg';
import check_circle_icon from '../assets/check_circle.svg';
import google_play_icon from '../assets/google_play.svg';
import { ContextState, useAuthValue } from '../context/AuthContext';
import SignupLayout from '../layout/SignupLayout';
import { FirebaseService } from '../services/firebase';
import { isFleetcare, isMindy, isOms } from '../utils';

export const VerifyEmail: React.FC = () => {
    const { idpUser: currentUser, verificationSent, setVerificationSent } = useAuthValue() as ContextState;

    const { t } = useTranslation();
    const [time, setTime] = useState(10);
    const navigate = useNavigate();

    // Checks upon mount whether user email verification status changes
    useEffect(() => {
        const interval = setInterval(() => {
            currentUser
                ?.reload()
                .then(() => {
                    if (currentUser?.emailVerified) {
                        clearInterval(interval);
                        navigate('/login');
                    }
                })
                .catch((err) => {
                    console.error(err);
                });
        }, 2000);
    }, [navigate, currentUser]);

    // Interval between retries
    useEffect(() => {
        let interval: NodeJS.Timer | null = null;
        if (verificationSent && time !== 0) {
            interval = setInterval(() => {
                setTime((time) => time - 1);
            }, 1000);
        } else if (time === 0) {
            setVerificationSent(false);
            setTime(10);
            if (interval) clearInterval(interval);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [verificationSent, time, setVerificationSent]);

    // Btn handler
    const resendEmailVerification = async () => {
        if (currentUser) {
            try {
                const email = currentUser.email ?? '';
                await FirebaseService.sendVerificationEmail(email);
                setVerificationSent(true);
            } catch (error) {
                alert(error);
            }
        } else {
            alert(t('verifyEmail.errors.auth'));
        }
    };

    if (isMindy || isFleetcare || isOms) {
        return (
            <SignupLayout>
                <div className="w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="space-y-5"
                    >
                        <MdMarkEmailRead className="text-primary-400 text-5xl" />
                        <div className="border-t border-gray-700" />
                        <div>
                            <h2 className="text-2xl font-poppins font-semibold text-white mb-2">
                                {t('verifyEmail.title')}
                            </h2>
                            <p className="text-sm text-gray-400 font-roboto">{t('verifyEmail.body')}:</p>
                            <p className="text-sm text-white font-poppins mt-1">{currentUser?.email}</p>
                        </div>
                        <div className="border-t border-gray-700" />
                        <button
                            onClick={resendEmailVerification}
                            disabled={verificationSent}
                            className="w-full bg-primary-500 hover:bg-primary-400 disabled:opacity-50 text-white font-poppins font-semibold py-3 rounded-lg transition-colors"
                        >
                            {t('verifyEmail.sendAgain')} {verificationSent && time}
                        </button>
                    </motion.div>
                </div>
            </SignupLayout>
        );
    }

    return (
        <SignupLayout>
            <div className="flex h-screen">
                <div className="m-auto text-center">
                    <img src={check_circle_icon} alt="" className="mx-auto" />
                    <strong className="text-2xl mb-10">{t('verifyEmail.title')}</strong>
                    <p>{t('verifyEmail.body')}:</p>
                    <p className="p-4">{currentUser?.email}</p>
                    <div className="mb-10">
                        <button
                            onClick={resendEmailVerification}
                            disabled={verificationSent}
                            className="rounded-md border-2 border-blue-600 text-lg font-semibold w-1/3"
                        >
                            <p className="p-2 font-['Poppins'] text-blue-600">
                                {t('verifyEmail.sendAgain')} {verificationSent && time}
                            </p>
                        </button>
                    </div>
                    <div className="mt-10 hidden">
                        <hr />
                        <p className="text-center">
                            También podés ingresar a ver como va tu operatoria descargando la{' '}
                            <strong>App del Mánager</strong>:
                        </p>
                        <div className="flex justify-center">
                            <img src={google_play_icon} alt="" />
                            <img src={apple_store_icon} alt="" className="m-4" />
                        </div>
                    </div>
                </div>
            </div>
        </SignupLayout>
    );
};

export default VerifyEmail;
